<template>
  <div class="dashboard">
    <!-- Header Card -->
    <div class="header-card">
      <div class="header-left">
        <template>
          <img style="width: 64px; height: 64px; border-radius: 50%; padding: 2px; border: 2px solid gray"
            :src="currentUser.profile_picture || 'static/img/default-dp.png'" alt="avatar"
            @click="$router.replace(`/user/${currentUser.uid}`)">
        </template>

        <div class="employee-info">
          <h1 class="emp-name">{{ currentUser.username }}</h1>
          <p class="emp-role">{{ currentUser.designation }}</p>
          <div class="badges">
            <!-- <span class="badge">EMP-{{ currentUser.uid }}</span> -->
            <span class="badge">Joined {{ currentUser.joiningDate }}</span>
            <span class="badge">{{ getWorkingDaysText() }}</span>
          </div>
        </div>
      </div>
      <div class="header-right">
        <span class="status-btn">Checked In</span>
        <div class="checkin-time">
          <span class="time">{{ checkIn.checkedIn ? checkIn.time : '--:-- --' }}</span>
          <span class="time-label">{{ checkIn.checkedIn ? "Today's check-in time" : "Not checked in yet" }}</span>
        </div>
      </div>
    </div>

    <!-- Stats Row -->
    <div class="stats-row">
      <div class="stat-card" v-for="stat in stats" :key="stat.label">
        <template v-if="loading.stats">
          <div class="sk sk-label"></div>
          <div class="sk sk-value"></div>
          <div class="sk sk-sub"></div>
          <div class="sk sk-bar"></div>
        </template>
        <template v-else>
          <p class="stat-label">{{ stat.label }}</p>
          <p class="stat-value" :style="{ color: stat.color }">{{ stat.value }}</p>
          <p class="stat-sub">{{ stat.sub }}</p>
          <div class="stat-bar-track">
            <div class="stat-bar-fill" :style="{ width: stat.progress + '%', backgroundColor: stat.barColor }"></div>
          </div>
        </template>
      </div>
    </div>

    <!-- Bottom Panels -->
    <div class="panels-row">
      <!-- Performance -->
      <div class="panel panel-performance">
        <div class="panel-header">
          <div>
            <p class="panel-title">Performance</p>
            <p class="panel-subtitle">RECENT MONTHS</p>
          </div>
          <a href="#/performance" class="panel-link">View All →</a>
        </div>
        <div class="perf-list">
          <template v-if="loading.performance">
            <div class="perf-row" v-for="n in 4" :key="'sk-perf-' + n">
              <div class="sk sk-month"></div>
              <div class="sk sk-bar-lg"></div>
              <div class="sk sk-pct"></div>
              <div class="sk sk-badge"></div>
            </div>
          </template>
          <template v-else>
            <div class="perf-row" v-for="p in performance" :key="p.month">
              <span class="perf-month">{{ p.month }}</span>
              <div class="perf-bar-track">
                <div class="perf-bar-fill" :style="{ width: p.pct + '%', backgroundColor: p.color }"></div>
              </div>
              <span class="perf-pct">{{ p.display }}</span>
              <span class="perf-badge" :class="p.badgeClass">{{ p.label }}</span>
            </div>
          </template>
        </div>
      </div>

      <!-- Upcoming Events -->
      <div class="panel panel-events">
        <div class="panel-header">
          <div>
            <p class="panel-title">Upcoming</p>
            <p class="panel-subtitle">EVENTS &amp; MEETINGS</p>
          </div>
          <a href="https://calendar.google.com/calendar/u/0/r" target="_blank" class="panel-link">Calendar →</a>
        </div>
        <div class="event-list">
          <template v-if="loading.events">
            <div class="event-row" v-for="n in 3" :key="'sk-evt-' + n">
              <div class="event-date">
                <div class="sk sk-day"></div>
                <div class="sk sk-mon"></div>
              </div>
              <div class="event-details">
                <div class="sk sk-title"></div>
                <div class="sk sk-desc"></div>
                <div class="sk sk-tag"></div>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="event-row" v-for="(ev, i) in events" :key="i">
              <div class="event-date">
                <span class="event-day">{{ ev.day }}</span>
                <span class="event-month">{{ ev.month }}</span>
              </div>
              <div class="event-details">
                <p class="event-title">{{ ev.title }}</p>
                <p class="event-desc">{{ ev.desc }}</p>
                <span class="event-tag" :class="ev.tagClass">{{ ev.tag }}</span>
              </div>
            </div>
            <p v-if="!events.length" class="event-empty">No upcoming events</p>
          </template>
        </div>
      </div>
    </div>

    <!-- Recent Payslips -->
    <div class="panels-row panels-row-single">
      <div class="panel panel-payslips">
        <div class="panel-header">
          <div>
            <p class="panel-title">Recent Payslips</p>
            <p class="panel-subtitle">DOWNLOAD HISTORY</p>
          </div>
          <a href="#/payroll" class="panel-link">View All →</a>
        </div>
        <div class="payslip-list" :class="{ 'payslip-list-single': !loading.payslips && payslips.length <= 3 }">
          <template v-if="loading.payslips">
            <div class="payslip-row" v-for="n in 6" :key="'sk-pay-' + n">
              <div class="sk sk-month"></div>
              <div class="sk sk-amount"></div>
              <div class="sk sk-btn"></div>
            </div>
          </template>
          <template v-else>
            <div class="payslip-row" v-for="slip in payslips" :key="slip.key">
              <span class="payslip-month">{{ slip.month }}</span>
              <span class="payslip-amount">{{ slip.amount }}</span>
              <button class="download-btn" :disabled="slip.downloading" @click="downloadPayslip(slip)">
                {{ slip.downloading ? '...' : 'Download' }}
              </button>
            </div>
            <p v-if="!payslips.length" class="payslip-empty">No processed payslips yet</p>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import firebase from "firebase/app";
import firebaseDB from "../../../assets/config/firebase";
import moment from "moment";
import Swal from "sweetalert";
import { settings } from "../../../utils/genralSpecificSettings";

export default {
  name: 'EmployeeDashboard',
  data() {
    return {
      currentUser: this.$store.state.auth.user,
      checkIn: { checkedIn: false, time: null },
      stats: [
        {
          label: 'ANNUAL LEAVES LEFT',
          value: '14',
          sub: 'of 21 total',
          color: '#1a1a1a',
          progress: 67,
          barColor: '#2ec4a9',
        },
        {
          label: 'PENDING LEAVE',
          value: '1',
          sub: 'awaiting approval',
          color: '#e0a800',
          progress: 20,
          barColor: '#e0a800',
        },
        {
          label: 'PERFORMANCE',
          value: '--',
          sub: 'no data',
          color: '#1a1a1a',
          progress: 0,
          barColor: '#2ec4a9',
        },
        {
          label: 'HOURS THIS WEEK',
          value: '18h',
          sub: 'of 40h target',
          color: '#1a1a1a',
          progress: 45,
          barColor: '#2ec4a9',
        },
      ],
      performance: [],
      events: [],
      payslips: [],
      loading: {
        stats: true,
        performance: true,
        events: true,
        payslips: true,
      },
      holidayList: []
    };
  },
  async mounted() {
    this.checkIn = await this.getTodayCheckIn(this.$store.state.auth.user.uid);

    this.stats[0].value = this.$store.state.auth.user.leavesAvailable - Number(this.$store.state.auth.user.leavesTaken);
    this.stats[0].sub = `of ${this.$store.state.auth.user.leavesAvailable} total`;
    this.stats[0].progress = ((this.$store.state.auth.user.leavesAvailable - Number(this.$store.state.auth.user.leavesTaken)) / this.$store.state.auth.user.leavesAvailable) * 100;

    this.stats[1].value = this.$store.state.auth.userLeaves.filter(leave => leave.status.toLowerCase() === 'pending').length || 0;
    this.stats[1].progress = (this.stats[1].value / this.$store.state.auth.user.leavesAvailable) * 100;

    this.loading.stats = false;

    // Each section resolves its own flag so a slow query in one card does not
    // hold back the others.
    this.loadSection('events', this.fetchUpcomingEvents());
    this.loadSection('performance', this.fetchPerformanceData());
    this.loadSection('payslips', this.fetchRecentPayslips());

    const weekHours = await this.getUserCurrentWeekHours(this.$store.state.auth.user.uid);
    this.stats[3].value = `${weekHours.spentHours}h`;
    this.stats[3].sub = `of ${weekHours.totalRequiredHours}h target`;
    this.stats[3].progress = (weekHours.spentDecimal / weekHours.totalRequiredHours) * 100;
  },
  methods: {
    // Tracks one card's loading state, always clearing it even on failure so a
    // failed query leaves an empty card rather than a permanent skeleton.
    async loadSection(key, promise) {
      try {
        await promise;
      } catch (err) {
        console.error(`Error loading ${key}:`, err);
      } finally {
        this.loading[key] = false;
      }
    },

    // Payroll lives at payroll/{year}/{month}/{pushKey}. The employee query is
    // scoped with orderByChild('employeeId').equalTo(uid), matching the payroll
    // store, so this can only ever return the signed-in user's own records.
    async fetchRecentPayslips() {
      const user = this.$store.state.auth.user;
      if (!user || !user.uid) return;

      const now = new Date();
      const results = [];

      // Walk back month by month until 6 processed payslips are collected.
      for (let back = 0; back < 24 && results.length < 6; back++) {
        const date = new Date(now.getFullYear(), now.getMonth() - back, 1);
        const year = date.getFullYear();
        const month = moment(date).format('MMMM');

        try {
          const snapshot = await firebase.database()
            .ref(`payroll/${year}/${month}`)
            .orderByChild('employeeId')
            .equalTo(user.uid)
            .once('value');

          const records = snapshot.val();
          if (!records) continue;

          Object.keys(records).forEach(key => {
            const record = records[key];
            if (!record || record.status !== 'Processed') return;

            results.push({
              key: record.payrollId || key,
              month: `${record.month || month} ${record.year || year}`,
              amount: new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'PKR',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }).format(Number(record.finalAmount) || 0),
              // Declared up front so the per-row spinner is reactive in Vue 2.
              downloading: false,
              record,
            });
          });
        } catch (e) {
          console.error(`Error fetching payroll for ${month} ${year}:`, e);
        }
      }

      results.sort((a, b) => {
        const aDate = new Date(a.record.year, moment(a.record.month, 'MMMM').month());
        const bDate = new Date(b.record.year, moment(b.record.month, 'MMMM').month());
        return bDate - aDate;
      });

      this.payslips = results.slice(0, 6);
    },

    // Mirrors SalaryCalculation's handleDownloadClick: hand the record to the
    // payslip route through localStorage and open it in a new tab. The PDF is
    // generated there because html2canvas cannot capture a hidden element.
    // The storage key is suffixed with the payroll id so downloading a second
    // month cannot overwrite a payslip a previous tab has not read yet.
    async downloadPayslip(slip) {
      if (!slip || slip.downloading) return;

      const payslipKey = `${slip.record.employeeId}_${slip.key}`;
      const storageKey = `payslipData_${payslipKey}`;
      slip.downloading = true;

      try {
        localStorage.setItem(
          storageKey,
          JSON.stringify({
            employee: {
              ...slip.record,
              leavesQuota: slip.record.leavesQuota || { leavesEncashmentDays: 0 },
            },
            salaryData: {
              totalDaysSalary: Number(slip.record.perDaySalary) * Number(slip.record.daysWorked),
            },
          })
        );

        const payslipUrl = `${window.location.origin}/#/payroll/payslip/${payslipKey}`;
        window.open(payslipUrl, '_blank');
      } catch (e) {
        console.error('Error downloading payslip:', e);
        Swal('Error', 'Could not generate the payslip PDF.', 'error');
        localStorage.removeItem(storageKey);
      } finally {
        // The payslip tab clears its own key; release ours on navigate away.
        this.$once('hook:beforeDestroy', () => {
          localStorage.removeItem(storageKey);
        });
        setTimeout(() => { slip.downloading = false; }, 1500);
      }
    },

    // Builds the Upcoming panel from all three sources (leaves, meetings,
    // holidays) and keeps the whole list sorted by date.
    async fetchUpcomingEvents() {
      const [leaveEvents, meetingEvents, holidayEvents] = await Promise.all([
        this.buildLeaveEvents(),
        this.buildMeetingEvents(),
        this.buildHolidayEvents(),
      ]);

      this.events = [...leaveEvents, ...meetingEvents, ...holidayEvents]
        .sort((a, b) => a.sortDate - b.sortDate);
    },

    buildLeaveEvents() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      return (this.$store.state.auth.userLeaves || [])
        .filter(leave => ['Approved', 'Pending'].includes(leave.status))
        .map(leave => {
          const start = new Date(leave.startDate);
          if (isNaN(start)) return null;
          start.setHours(0, 0, 0, 0);

          return { start, leave };
        })
        .filter(item => item && item.start > today)
        .map(({ start, leave }) => {
          const isPending = leave.status === 'Pending';

          return {
            date: start,
            sortDate: start.getTime(),
            day: String(start.getDate()).padStart(2, '0'),
            month: start.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
            title: isPending ? 'Leave request pending' : 'Leave approved',
            desc: `${leave.type} — ${leave.noofdays === 1 ? '1 day' : `${leave.noofdays} days`}`,
            tag: isPending ? 'Awaiting' : 'Approved',
            tagClass: isPending ? 'tag-awaiting' : 'tag-approved',
          };
        });
    },

    // Meetings are scheduled per-employee by the admin from User Management
    // and stored on the user record. App.vue refreshes that record on mount, but
    // this component can render before that resolves, so read it fresh.
    async buildMeetingEvents() {
      let user = this.$store.state.auth.user;

      try {
        const snapshot = await firebase.database().ref(`users/${user.uid}`).once('value');
        const fresh = snapshot.val();
        if (fresh) {
          user = { ...user, ...fresh };
        }
      } catch (e) {
        console.error('Error reading meeting details:', e);
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const sources = [
        user.nextIncrementMeetingDetails,
        user.nextQuarterlyMeetingDetails,
      ];

      return sources
        .filter(meeting => meeting && meeting.date)
        .map(meeting => {
          const date = new Date(meeting.date);
          if (isNaN(date)) return null;
          date.setHours(0, 0, 0, 0);

          return { date, meeting };
        })
        .filter(item => item && item.date >= today)
        .map(({ date, meeting }) => {
          let time = '';
          if (meeting.time) {
            const [hourStr, minute = '00'] = meeting.time.split(':');
            const hour = parseInt(hourStr, 10);
            if (!isNaN(hour)) {
              time = `${hour % 12 || 12}:${minute} ${hour >= 12 ? 'PM' : 'AM'}`;
            }
          }

          const isIncrement = meeting.type === 'increment';

          return {
            date,
            sortDate: date.getTime(),
            day: String(date.getDate()).padStart(2, '0'),
            month: date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
            title: meeting.title || (isIncrement
              ? 'Increment review meeting'
              : 'Quarterly review meeting'),
            desc: time ? `With manager · ${time}` : 'With manager',
            tag: 'Meeting',
            tagClass: 'tag-meeting',
          };
        });
    },

    async buildHolidayEvents() {
      const holidays = await firebaseDB.getAllHolidays();
      const user = this.$store.state.auth.user;

      // Admins see every holiday; everyone else only their shift plus general.
      this.holidayList = user.role === 'admin'
        ? holidays
        : holidays.filter(h => this.isHolidayForUser(h, user));

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Only surface holidays in the current or next month. Without this a
      // recurring holiday rolls forward a whole year and floods the panel.
      const horizon = new Date(today.getFullYear(), today.getMonth() + 2, 0);
      horizon.setHours(23, 59, 59, 999);

      return holidays
        .filter(h => this.isHolidayForUser(h, user))
        .map(h => {
          const base = new Date(h.dates);
          if (isNaN(base)) return null;

          let start = new Date(base.getFullYear(), base.getMonth(), base.getDate());

          // A recurring holiday whose date already passed this year rolls
          // forward to its next occurrence, otherwise it would never reappear.
          if (h.isRecurring) {
            while (start <= today) {
              start = new Date(start.getFullYear() + 1, start.getMonth(), start.getDate());
            }
          }

          start.setHours(0, 0, 0, 0);

          return { ...h, start };
        })
        .filter(item => item && item.start > today && item.start <= horizon)
        .map(item => {
          const shift = (item.shift || '').toLowerCase();
          const scopeLabel =
            shift === 'general'
              ? 'Company-wide'
              : shift === 'night'
                ? 'Night shift'
                : 'Morning shift';

          const days = item.numberOfDays === 1 ? '1 day' : `${item.numberOfDays} days`;
          const start = item.start;

          return {
            date: start,
            sortDate: start.getTime(),
            day: String(start.getDate()).padStart(2, '0'),
            month: start.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
            title: item.name,
            desc: `${scopeLabel} · ${days}`,
            tag: 'Holiday',
            tagClass: 'tag-holiday',
          };
        });
    },

    isHolidayForUser(holiday, user) {
      const holidayShift = (holiday.shift || 'morning').toLowerCase();
      if (holidayShift === 'general') return true;

      const userShift = (user.shift || 'morning').toLowerCase();
      return userShift === holidayShift;
    },


    // The last `count` completed months relative to today, newest first.
    getRecentMonths(count) {
      const now = new Date();

      return Array.from({ length: count }, (_, i) => {
        const date = new Date(now.getFullYear(), now.getMonth() - 1 - i, 1);
        return {
          month: moment(date).format("MMMM"),
          year: String(date.getFullYear()),
          short: moment(date).format("MMM").toUpperCase(),
        };
      });
    },

    // Resolves the score fields the same way Performance.vue's
    // normalizePerformanceCard does. saveEditedCard only writes updatedData,
    // never a top-level score/reopenCount, so the flat fields alone are not
    // enough for items that were added and then edited.
    resolveCardFields(card) {
      const original = card.originalData || {};
      const updated = card.updatedData || {};

      const pick = (value, ...fallbacks) => {
        if (value !== null && value !== undefined) return value;
        return fallbacks.find(v => v !== null && v !== undefined);
      };

      return {
        score: Number(pick(card.score, updated.score, original.score) || 0),
        reopenCount: Number(
          pick(card.reopenCount, updated.reopened, original.reopenCount) || 0
        ),
        originalreopenCount: Number(
          pick(card.originalreopenCount, original.reopenCount) || 0
        ),
        deadlineMissedCount: Number(
          pick(card.deadlineMissedCount, updated.deadlineMissedCount) || 0
        ),
        status: card.status || updated.status || "approved",
      };
    },

    // Net score per month, mirroring Performance.vue's tableTotals + summaryCards.
    // The `performance` node is read once and grouped in memory, since
    // fetchEditedCards always pulls the whole node regardless of the filters.
    async getScoresByMonth(userId, ranges) {
      const cards = await firebaseDB.fetchEditedCards({ userId });
      const rows = Object.values(cards || {});

      const byMonth = {};
      ranges.forEach(({ month, year }) => {
        byMonth[`${year}-${month}`] = { rawScore: 0, netScore: 0, itemCount: 0 };
      });

      if (!rows.length) return byMonth;

      const criteria = await firebaseDB.getAllPerformanceCriteria();
      const ticketCrit = (criteria || []).find(c => c.type === "Ticket");
      const penalties = (ticketCrit && ticketCrit.rules && ticketCrit.rules.penalties) || {};
      const reopens = penalties.reopens || {};
      const deadlineMissed = penalties.deadlineMissed || {};

      const counts = {};

      rows.forEach(card => {
        if (card.ticketStatus === "pending") return;

        const key = `${card.year}-${card.month}`;
        const bucket = byMonth[key];
        if (!bucket) return;

        const fields = this.resolveCardFields(card);

        bucket.itemCount += 1;
        bucket.rawScore += fields.score;

        counts[key] = counts[key] || { reopened: 0, deadlineMissed: 0 };
        counts[key].reopened +=
          fields.status !== "pending" && fields.status !== "rejected"
            ? fields.reopenCount
            : fields.originalreopenCount;
        counts[key].deadlineMissed += fields.deadlineMissedCount;
      });

      // Penalty scores are negative, so they are added to the raw score.
      Object.keys(byMonth).forEach(key => {
        const bucket = byMonth[key];
        const totals = counts[key] || { reopened: 0, deadlineMissed: 0 };

        const reopenDeduction = reopens.limit && reopens.score
          ? (totals.reopened >= reopens.limit ? reopens.score : 0)
          : (reopens.every && reopens.score
              ? Math.floor(totals.reopened / reopens.every) * reopens.score
              : 0);

        const deadlineDeduction = deadlineMissed.limit && deadlineMissed.score
          ? (totals.deadlineMissed >= deadlineMissed.limit ? deadlineMissed.score : 0)
          : (deadlineMissed.every && deadlineMissed.score
              ? Math.floor(totals.deadlineMissed / deadlineMissed.every) * deadlineMissed.score
              : 0);

        bucket.netScore = bucket.rawScore + reopenDeduction + deadlineDeduction;
      });

      return byMonth;
    },

    // Target score comes from the employee's designation + grade.
    async getTargetScore(designation, grade) {
      if (!designation || !grade) return null;

      const targets = await firebaseDB.getAllPerformanceScoresByGrade();
      const match = (targets || []).find(
        t => t.designation === designation && String(t.grade) === String(grade)
      );

      if (!match || !Number(match.score)) return null;
      return Number(match.score);
    },

    getPerformanceRating(pct) {
      if (pct >= 100) return { label: "Excellent", color: "#28a06a", badgeClass: "badge-excellent" };
      if (pct >= 80) return { label: "Good", color: "#2ec4a9", badgeClass: "badge-good" };
      if (pct >= 60) return { label: "Avg", color: "#5b85c3", badgeClass: "badge-avg" };
      if (pct >= 40) return { label: "Fair", color: "#e0a800", badgeClass: "badge-fair" };
      return { label: "Poor", color: "#e05c5c", badgeClass: "badge-poor" };
    },

    async fetchPerformanceData() {
      const user = this.$store.state.auth.user;
      if (!user || !user.uid) return;

      const ranges = this.getRecentMonths(4);
      const stat = this.stats[2];

      const [scores, targetScore] = await Promise.all([
        this.getScoresByMonth(user.uid, ranges),
        this.getTargetScore(user.designation, user.grade),
      ]);

      this.performance = ranges.map(({ month, year, short }) => {
        const result = scores[`${year}-${month}`] || { netScore: 0, itemCount: 0 };

        if (!targetScore || !result.itemCount) {
          return {
            month: short,
            pct: 0,
            display: "--",
            color: "#c3ccc9",
            label: "No data",
            badgeClass: "badge-none",
          };
        }

        const pct = Math.max(0, Math.round((result.netScore / targetScore) * 100));
        const rating = this.getPerformanceRating(pct);

        return {
          month: short,
          netScore: result.netScore,
          pct: Math.min(pct, 100),
          display: `${pct}%`,
          color: rating.color,
          label: rating.label,
          badgeClass: rating.badgeClass,
        };
      });

      // The stat card shows the most recent completed month.
      const latest = this.performance[0];

      if (!targetScore || latest.display === "--") {
        stat.label = "PERFORMANCE";
        stat.value = "--";
        stat.sub = targetScore ? "no records last month" : "no target score set";
        stat.color = "#1a1a1a";
        stat.progress = 0;
        stat.barColor = "#2ec4a9";
        return;
      }

      stat.label = `PERFORMANCE (${latest.month})`;
      stat.value = latest.display;
      stat.sub = `${latest.netScore.toFixed(1)} of ${targetScore} target`;
      stat.color = latest.color;
      stat.progress = latest.pct;
      stat.barColor = latest.color;
    },

    getWorkingDaysText() {
      const days = this.$store.state.auth.user.workingDays || this.$store.state.auth.constants.days || [];

      const weekOrder = [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday"
      ];

      const shortDays = {
        Monday: "Mon",
        Tuesday: "Tue",
        Wednesday: "Wed",
        Thursday: "Thu",
        Friday: "Fri",
        Saturday: "Sat",
        Sunday: "Sun"
      };

      const sortedDays = [...days].sort(
        (a, b) => weekOrder.indexOf(a) - weekOrder.indexOf(b)
      );

      if (!sortedDays.length) return "";

      const indexes = sortedDays.map(day => weekOrder.indexOf(day));

      const isContinuous = indexes.every(
        (index, i) => i === 0 || index === indexes[i - 1] + 1
      );

      if (isContinuous && sortedDays.length > 1) {
        return `${shortDays[sortedDays[0]]} – ${shortDays[sortedDays[sortedDays.length - 1]]
          }`;
      }

      const shortNames = sortedDays.map(day => shortDays[day]);

      if (shortNames.length === 1) {
        return shortNames[0];
      }

      if (shortNames.length === 2) {
        return `${shortNames[0]} and ${shortNames[1]}`;
      }

      return `${shortNames.slice(0, -1).join(", ")} and ${shortNames[shortNames.length - 1]
        }`;
    },
    getTodayCheckIn: async (userId) => {
      const now = new Date();
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0).getTime();
      const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59).getTime();

      const snapshot = await firebase.database()
        .ref("timePeriod")
        .orderByChild("timeIn")
        .startAt(startOfDay)
        .endAt(endOfDay)
        .once("value");

      const value = snapshot.val();

      if (!value) return { checkedIn: false, time: null };

      const entry = Object.values(value).find(log => log.userId === userId);

      if (!entry) return { checkedIn: false, time: null };

      return {
        checkedIn: true,
        time: moment(entry.timeIn).format("hh:mm A")
      };
    },
    async getUserCurrentWeekHours(userId) {
      const now = new Date();
      const dayOfWeek = now.getDay();

      const monday = new Date(now);
      monday.setDate(now.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
      monday.setHours(0, 0, 0, 0);

      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      sunday.setHours(23, 59, 59, 999);

      const startTime = monday.getTime();
      const endTime = sunday.getTime();

      const settingsResponse = await settings(userId, this.$store.state.auth.constants);
      const workingDays = (settingsResponse && settingsResponse.days) || [
        "Monday", "Tuesday", "Wednesday", "Thursday", "Friday"
      ];
      const workingHoursPerDay = Number((settingsResponse && settingsResponse.totalHours)) || 9;

      // ✅ Count ALL 5 working days of the week, not just up to today
      let requiredWorkingDays = 0;
      for (let d = new Date(monday); d <= sunday; d.setDate(d.getDate() + 1)) {
        const dayName = d.toLocaleDateString("en-US", { weekday: "long" });
        if (workingDays.includes(dayName)) {
          requiredWorkingDays++;
        }
      }
      const totalRequiredHours = requiredWorkingDays * workingHoursPerDay;

      const database = firebase.database();
      const snapshot = await database
        .ref("timePeriod")
        .orderByChild("timeIn")
        .startAt(startTime)
        .endAt(endTime)
        .once("value");

      const value = snapshot.val();

      // ✅ Debug: see what Firebase actually returned
      console.log("Firebase week logs:", value);

      let totalSpentMs = 0;

      if (value) {
        Object.values(value).forEach((log) => {
          if (log.userId !== userId) return;

          if (log.sessions && Array.isArray(log.sessions)) {
            log.sessions.forEach((session, index) => {
              if (!session.timeIn || !Number.isFinite(session.timeIn)) return;

              const sessionTimeOut =
                session.timeOut ||
                (index === log.sessions.length - 1 ? log.latestScreenshotTime : null);

              if (sessionTimeOut && Number.isFinite(sessionTimeOut) && sessionTimeOut > session.timeIn) {
                totalSpentMs += sessionTimeOut - session.timeIn;
              }
            });
          }
        });
      }

      const totalSpentSeconds = Math.floor(totalSpentMs / 1000);
      const spentHours = Math.floor(totalSpentSeconds / 3600);
      const spentMinutes = Math.floor((totalSpentSeconds % 3600) / 60);
      const spentDecimal = spentHours + spentMinutes / 60;
      const netBalance = spentDecimal - totalRequiredHours;

      return {
        weekRange: { from: monday.toDateString(), to: sunday.toDateString() },
        requiredWorkingDays,         // should now be 5
        totalRequiredHours,          // should now be 45
        spentHours,
        spentMinutes,
        spentDecimal: parseFloat(spentDecimal.toFixed(2)),
        netBalance: parseFloat(netBalance.toFixed(2)),
        isDeficit: netBalance < 0,
      };
    },
  }
};
</script>

<style scoped>
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.dashboard {
  min-height: 100vh;
  padding: 24px;
  color: #1a1a1a;
}

/* ── Header ── */
.header-card {
  background: #3F4C49;
  border-radius: 12px;
  padding: 24px 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
  color: #fff;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 18px;
}

.avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #56C0A5;
  color: #1e2d2b;
  font-weight: 700;
  font-size: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.emp-name {
  font-size: 22px;
  font-weight: 700;
  color: #fff;
}

.emp-role {
  color: #a8bab8;
  font-size: 14px;
  margin: 3px 0 8px;
}

.badges {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.badge {
  background: transparent;
  border: 1px solid #56C0A5;
  color: #a8bab8;
  border-radius: 4px;
  padding: 2px 10px;
  font-size: 12px;
}

.header-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}

.checkin-time {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}

.status-btn {
  border: 1.5px solid #56C0A5;
  color: #56C0A5;
  border-radius: 6px;
  padding: 4px 14px;
  font-size: 13px;
  font-weight: 500;
}

.time {
  font-size: 32px;
  font-weight: 700;
  color: #fff;
  line-height: 1;
}

.time-label {
  font-size: 12px;
  color: #a8bab8;
  text-align: right;
}

/* ── Stats Row ── */
.stats-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

.stat-card {
  background: #fff;
  border-radius: 10px;
  padding: 20px 20px 16px;
}

.stat-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  color: #8a9a98;
  margin-bottom: 6px;
  text-transform: uppercase;
}

.stat-value {
  font-size: 32px;
  font-weight: 700;
  line-height: 1.1;
}

.stat-sub {
  font-size: 12px;
  color: #8a9a98;
  margin: 2px 0 12px;
}

.stat-bar-track {
  height: 5px;
  background: #e8eceb;
  border-radius: 99px;
  overflow: hidden;
}

.stat-bar-fill {
  height: 100%;
  border-radius: 99px;
  transition: width 0.4s ease;
}

/* ── Skeletons ── */
.sk {
  border-radius: 6px;
  background: linear-gradient(90deg, #eef1f0 25%, #e2e7e6 50%, #eef1f0 75%);
  background-size: 200% 100%;
  animation: sk-shimmer 1.4s infinite;
}

@keyframes sk-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

@media (prefers-reduced-motion: reduce) {
  .sk {
    animation: none;
  }
}

/* Stat card */
.sk-label { width: 60%; height: 10px; margin-bottom: 14px; }
.sk-value { width: 45%; height: 26px; margin-bottom: 8px; }
.sk-sub { width: 70%; height: 10px; margin-bottom: 14px; }
.sk-bar { width: 100%; height: 6px; border-radius: 99px; }

/* Performance row */
.sk-month { width: 34px; height: 12px; flex-shrink: 0; }
.sk-bar-lg { height: 8px; border-radius: 99px; flex: 1; }
.sk-pct { width: 30px; height: 12px; flex-shrink: 0; }
.sk-badge { width: 68px; height: 20px; border-radius: 4px; flex-shrink: 0; }

/* Event row */
.sk-day { width: 26px; height: 16px; }
.sk-mon { width: 26px; height: 8px; margin-top: 5px; }
.sk-title { width: 65%; height: 12px; margin-bottom: 7px; }
.sk-desc { width: 45%; height: 10px; margin-bottom: 8px; }
.sk-tag { width: 58px; height: 16px; border-radius: 4px; }

/* Payslip row */
.sk-amount { width: 80px; height: 12px; }
.sk-btn { width: 74px; height: 30px; border-radius: 6px; }

/* ── Panels ── */
.panels-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;
}

/* Single-panel row (e.g. Recent Payslips) spans the full width. */
.panels-row-single {
  grid-template-columns: 1fr;
}

.panel {
  background: #fff;
  border-radius: 10px;
  padding: 22px 22px 18px;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 18px;
}

.panel-title {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a1a;
}

.panel-subtitle {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  color: #8a9a98;
  margin-top: 2px;
}

.panel-link {
  font-size: 13px;
  color: #3D5851;
  text-decoration: none;
  font-weight: 500;
  white-space: nowrap;
}

/* ── Performance ── */
.perf-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}

.perf-month {
  font-size: 13px;
  color: #555;
  width: 28px;
  flex-shrink: 0;
}

.perf-bar-track {
  flex: 1;
  height: 8px;
  background: #e8eceb;
  border-radius: 99px;
  overflow: hidden;
}

.perf-bar-fill {
  height: 100%;
  border-radius: 99px;
  transition: width 0.4s ease;
}

.perf-pct {
  font-size: 13px;
  color: #555;
  width: 36px;
  text-align: right;
  flex-shrink: 0;
}

.perf-badge {
  font-size: 11px;
  font-weight: 600;
  border-radius: 4px;
  padding: 2px 8px;
  width: 68px;
  text-align: center;
  flex-shrink: 0;
}

.badge-good {
  background: #e6f7f4;
  color: #2ec4a9;
}

.badge-avg {
  background: #eaf0f9;
  color: #5b85c3;
}

.badge-excellent {
  background: #e4f4ee;
  color: #28a06a;
}

.badge-fair {
  background: #fff8e1;
  color: #c8920a;
}

.badge-poor {
  background: #fdecec;
  color: #e05c5c;
}

.badge-none {
  background: #f1f4f3;
  color: #98a5a2;
}

/* ── Events ── */
.event-row {
  display: flex;
  gap: 14px;
  margin-bottom: 16px;
}

.event-date {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 36px;
}

.event-day {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1;
}

.event-month {
  font-size: 10px;
  font-weight: 600;
  color: #8a9a98;
  letter-spacing: 0.05em;
}

.event-title {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 2px;
}

.event-desc {
  font-size: 12px;
  color: #8a9a98;
  margin-bottom: 5px;
}

.event-tag {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  border-radius: 4px;
  padding: 2px 10px;
}

.tag-awaiting {
  background: #fff3cd;
  color: #c8920a;
}

.tag-meeting {
  background: #e4edff;
  color: #3b6dd4;
}

.tag-holiday {
  background: #e6f7f4;
  color: #2ec4a9;
}

.tag-approved {
  background: #e4f4ee;
  color: #28a06a;
}

.event-empty {
  font-size: 13px;
  color: #8a9a98;
  margin: 4px 0 0;
}

/* ── Payslips ── */
.payslip-list {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 28px;
}

/* Three or fewer payslips stack in one full-width column instead of being
   split across two half-width columns. */
.payslip-list-single {
  grid-template-columns: 1fr;
}

.payslip-row {
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #f0f2f1;
}

/* With two columns the last item in each column has no row beneath it. */
.payslip-row:nth-last-child(-n + 2) {
  border-bottom: none;
}

/* In single-column mode only the final item should lose its divider. */
.payslip-list-single .payslip-row:nth-last-child(-n + 2) {
  border-bottom: 1px solid #f0f2f1;
}

.payslip-list-single .payslip-row:last-child {
  border-bottom: none;
}

.payslip-month {
  font-size: 14px;
  color: #1a1a1a;
}

.payslip-amount {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
  min-width: 80px;
  text-align: right;
}

.download-btn {
  background: #3D5851;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 7px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.download-btn:hover {
  background: #56C0A5;
}

.download-btn:disabled {
  background: #9aa5a3;
  cursor: not-allowed;
}

.payslip-empty {
  font-size: 13px;
  color: #8a9a98;
  margin: 4px 0 0;
}

/* ── Responsive ── */
@media (max-width: 900px) {
  .stats-row {
    grid-template-columns: repeat(2, 1fr);
  }

  /* Two payslip columns get too cramped to stay readable side by side. */
  .payslip-list {
    grid-template-columns: 1fr;
  }

  .payslip-row:nth-last-child(-n + 2) {
    border-bottom: 1px solid #f0f2f1;
  }

  .payslip-row:last-child {
    border-bottom: none;
  }
}

@media (max-width: 640px) {
  .dashboard {
    padding: 14px;
  }

  .stats-row {
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  .panels-row {
    grid-template-columns: 1fr;
  }

  .header-card {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-right {
    align-items: flex-start;
  }

  .time-label {
    text-align: left;
  }
}

@media (max-width: 400px) {
  .stats-row {
    grid-template-columns: 1fr;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/Dashboard/Views/EmployeeDashboard.vue